import React, { useCallback, useEffect, useState } from "react";
import { getPublicPosts } from "../../api";
import useGoToPricing from "../../hooks/useGoToPricing";
import { isLoggedIn } from "../../utils/isLoggedIn";
import { scrollPageToTop } from "../../utils/scrollPageToTop";
import Header from "../Homepage/sections/Header";
import Footer from "../Homepage/sections/Footer";
import { HomepageGlobalStyles } from "../Homepage/styles";
import MembersPagination from "../Members/MembersPagination";
import PublicBlogCard from "./PublicBlogCard";
import PublicBlogSkeleton from "./PublicBlogSkeleton";
import { PUBLIC_BLOG_PAGE_SIZE } from "./publicBlogUtils";
import {
  PublicBlogContentInner,
  PublicBlogCta,
  PublicBlogCtaBg,
  PublicBlogCtaButton,
  PublicBlogCtaInner,
  PublicBlogCtaLead,
  PublicBlogCtaTitle,
  PublicBlogEmptyState,
  PublicBlogFeedSection,
  PublicBlogHero,
  PublicBlogHeroBgGradient,
  PublicBlogHeroBgRadial,
  PublicBlogHeroInner,
  PublicBlogHeroLead,
  PublicBlogHeroTitle,
  PublicBlogPage,
  PublicBlogPaginationWrap,
} from "./PublicBlog.styles";

const PublicBlog = () => {
  const goToPricing = useGoToPricing();
  const loggedIn = isLoggedIn();

  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [activePage, setActivePage] = useState(1);
  const [loading, setLoading] = useState(true);

  const totalPages = Math.max(1, Math.ceil(total / PUBLIC_BLOG_PAGE_SIZE));

  const fetchPosts = useCallback(async (pageIndex) => {
    setLoading(true);
    try {
      const response = await getPublicPosts(
        "*",
        PUBLIC_BLOG_PAGE_SIZE,
        pageIndex
      );
      setPosts(response?.data?.items || []);
      setTotal(response?.data?.total || 0);
      setActivePage(pageIndex + 1);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchPosts(0);
  }, [fetchPosts]);

  const handlePageChange = (pageNumber) => {
    fetchPosts(pageNumber - 1);
    scrollPageToTop();
  };

  return (
    <PublicBlogPage>
      <HomepageGlobalStyles />
      <Header onSubscribeClick={goToPricing} hideSpacer />

      <PublicBlogHero>
        <PublicBlogHeroBgGradient aria-hidden="true" />
        <PublicBlogHeroBgRadial aria-hidden="true" />
        <PublicBlogHeroInner>
          <PublicBlogHeroTitle>
            The Bitcoin.live <span>Public Blog</span>
          </PublicBlogHeroTitle>
          <PublicBlogHeroLead>
            Free commentary and education from our analyst team on Bitcoin and
            the broader crypto markets.
          </PublicBlogHeroLead>
        </PublicBlogHeroInner>
      </PublicBlogHero>

      <PublicBlogFeedSection>
        {loading ? (
          <PublicBlogSkeleton />
        ) : (
          <PublicBlogContentInner>
            {posts.length === 0 ? (
              <PublicBlogEmptyState>
                There are no public posts to show right now. Check back soon.
              </PublicBlogEmptyState>
            ) : (
              <>
                {posts.map((post) => (
                  <PublicBlogCard key={post.id} post={post} />
                ))}
                {totalPages > 1 && (
                  <PublicBlogPaginationWrap>
                    <MembersPagination
                      activePage={activePage}
                      totalPages={totalPages}
                      onChange={handlePageChange}
                    />
                  </PublicBlogPaginationWrap>
                )}
              </>
            )}
          </PublicBlogContentInner>
        )}
      </PublicBlogFeedSection>

      {!loggedIn && (
        <PublicBlogCta>
          <PublicBlogCtaBg aria-hidden="true" />
          <PublicBlogCtaInner>
            <PublicBlogCtaTitle>
              Want the <span>full picture?</span>
            </PublicBlogCtaTitle>
            <PublicBlogCtaLead>
              Members get daily video commentary, trade-level detail, and
              direct access to all seven analysts.
            </PublicBlogCtaLead>
            <PublicBlogCtaButton type="button" onClick={goToPricing}>
              Start Your Subscription
            </PublicBlogCtaButton>
          </PublicBlogCtaInner>
        </PublicBlogCta>
      )}

      <Footer onSubscribeClick={goToPricing} hideSubscribeCta={loggedIn} />
    </PublicBlogPage>
  );
};

export default PublicBlog;
